import React from 'react'
import { Link } from 'react-router-dom'
import { TIER_DISPLAY_NAMES, TIER_MONTHLY_PRICES } from '@treu/shared'
import FooterCopyright from '../components/FooterCopyright'
import HeroVerifyDemo from '../components/HeroVerifyDemo'
import VerdictSeal from '../components/VerdictSeal'

const PRICING_PLANS = [
  {
    tier: 'free',
    name: TIER_DISPLAY_NAMES.free,
    price: TIER_MONTHLY_PRICES.free,
    description: 'Get started with basic fact-checking',
    features: [
      '10 verifications/month',
      'AI-powered fact-checking',
      'Up to 2 trusted sources',
      'Web dashboard access',
    ],
  },
  {
    tier: 'basic',
    name: TIER_DISPLAY_NAMES.basic,
    price: TIER_MONTHLY_PRICES.basic,
    description: 'Essential verification for everyday use',
    features: [
      '100 verifications/month',
      'AI-powered fact-checking',
      'Up to 4 trusted sources',
      'Mobile + web access',
    ],
  },
  {
    tier: 'premium',
    name: TIER_DISPLAY_NAMES.premium,
    price: TIER_MONTHLY_PRICES.premium,
    description: 'Enhanced verification with priority features',
    features: [
      '200 verifications/month',
      'Deeper AI analysis',
      'Up to 7 trusted sources',
      'Chrome extension',
      'Analytics dashboard',
      'Community access (read-only)',
    ],
    popular: true,
  },
  {
    tier: 'professional',
    name: TIER_DISPLAY_NAMES.professional,
    price: TIER_MONTHLY_PRICES.professional,
    description: 'Unlimited verification for power users',
    features: [
      'Unlimited verifications',
      'Advanced AI analysis',
      '9+ trusted sources',
      'Chrome extension',
      'Full community access',
    ],
  },
]

const SOURCE_CATEGORIES = [
  'Scientific Research',
  'Medical Authorities',
  'Fact-Check Registries',
  'News & Media',
  'Reference Databases',
  'Government Sources',
]

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Enter any claim',
    description: 'Type or paste any claim, headline, or statement — from the web app, mobile app, or browser extension.',
  },
  {
    step: '02',
    title: 'The scan runs',
    description: 'Treu cross-references the claim against trusted scientific, medical, journalistic, and government databases simultaneously.',
  },
  {
    step: '03',
    title: 'A verdict resolves',
    description: 'You get a sealed verdict: confidence score, plain-language reasoning, and the sources behind it.',
  },
]

const FEATURES = [
  {
    title: 'Real-Time Verification',
    description: 'Results in seconds. Claims are checked against multiple trusted databases in parallel.',
  },
  {
    title: 'Trusted Source Network',
    description: 'Scientific, medical, governmental, and journalistic sources — more sources on higher plans.',
  },
  {
    title: 'Browser Extension',
    description: 'Verify claims directly on any webpage without switching apps. Chrome extension, on Premium+.',
  },
  {
    title: 'Mobile App',
    description: 'Verify on the go with the iOS and Android apps. Share anything you see straight into Treu.',
  },
  {
    title: 'Confidence Scoring',
    description: 'Every verdict carries a confidence score and a plain-language explanation of exactly why.',
  },
  {
    title: 'Discerning Minds',
    description: 'A community of critical thinkers who share verified facts and discuss nuanced topics. Read on Premium, post on Professional.',
  },
]

const Eyebrow: React.FC<{ children: React.ReactNode; onDark?: boolean }> = ({ children, onDark }) => (
  <p className={`font-display text-xs font-medium uppercase tracking-[0.25em] mb-4 ${onDark ? 'text-accent-300' : 'text-accent-600'}`}>
    {children}
  </p>
)

const LandingPage: React.FC = () => {
  return (
    <div className="text-ink-900 bg-paper-0">

      {/* ── Navigation ─────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-[100] h-16 px-4 md:px-8 flex items-center justify-between bg-brand-950 border-b border-white/10">
        <img src="/assets/logo-white.png" alt="Treu" className="h-7 w-auto" />
        <div className="flex items-center gap-4">
          <a href="#pricing" className="text-white/80 hover:text-white text-sm no-underline transition-colors duration-150">
            Pricing
          </a>
          <Link
            to="/login"
            className="px-5 py-2 rounded-md text-sm font-medium text-white bg-white/10 border border-white/20 hover:bg-white/15 no-underline transition-colors duration-150"
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* ── Hero: the product is the demo ──────────────────────── */}
      <section className="relative overflow-hidden bg-brand-950 pt-28 md:pt-36 pb-16 md:pb-24 px-4 md:px-8">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 70% 30%, rgb(var(--tr-accent-500) / 0.10) 0%, transparent 55%)' }}
        />
        <div className="relative max-w-[1150px] mx-auto grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
          <div className="text-center lg:text-left">
            <Eyebrow onDark>Truth, verified</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-bold text-white leading-[1.08] tracking-tight mb-6">
              Don't repeat it.<br />
              <span className="text-accent-300">Verify it.</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-[520px] mx-auto lg:mx-0 mb-9">
              Treu checks any claim against trusted scientific, medical, and
              journalistic sources in seconds — and hands you a verdict worth
              sharing. Be the person your network trusts to get it right.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-stretch sm:items-center max-w-sm sm:max-w-none mx-auto lg:mx-0">
              <Link
                to="/login"
                className="px-8 py-4 rounded-md text-base font-semibold text-white bg-accent-500 hover:bg-accent-600 no-underline transition-colors duration-150 text-center"
              >
                Get Started Free
              </Link>
              <a
                href="#how-it-works"
                className="px-8 py-4 rounded-md text-base font-medium text-white/90 bg-white/5 border border-white/15 hover:bg-white/10 no-underline transition-colors duration-150 text-center"
              >
                How It Works
              </a>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <HeroVerifyDemo />
          </div>
        </div>
      </section>

      {/* ── Trusted sources strip ──────────────────────────────── */}
      <section className="bg-paper-50 border-b border-paper-200 py-6 px-4 md:px-8 text-center">
        <p className="font-display text-xs font-medium uppercase tracking-[0.2em] text-ink-400 mb-4">
          Verified against trusted sources
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {SOURCE_CATEGORIES.map((category) => (
            <span
              key={category}
              className="px-3 py-1 rounded-full text-sm font-medium text-ink-600 bg-white border border-paper-200"
            >
              {category}
            </span>
          ))}
        </div>
      </section>

      {/* ── How it works: editorial two-column ─────────────────── */}
      <section id="how-it-works" className="py-16 md:py-28 px-4 md:px-8 bg-paper-0">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-10 md:gap-20">
          <div>
            <Eyebrow>The method</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-5 leading-tight">
              From claim to verdict in three moves.
            </h2>
            <p className="text-ink-600 text-lg leading-relaxed">
              No research rabbit holes. No twelve open tabs. Treu does the
              cross-referencing; you get the judgment.
            </p>
          </div>
          <div>
            {HOW_IT_WORKS.map((item, i) => (
              <div
                key={item.step}
                className={`flex gap-6 md:gap-10 py-8 ${i > 0 ? 'border-t border-paper-200' : 'pt-0 md:pt-2'}`}
              >
                <div className="font-display text-4xl md:text-5xl font-light text-accent-500/40 leading-none select-none">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-ink-900 mb-2">{item.title}</h3>
                  <p className="text-ink-600 leading-relaxed text-[0.95rem]">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The verdict: dark showcase band ────────────────────── */}
      <section className="bg-brand-950 py-16 md:py-28 px-4 md:px-8 overflow-hidden">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Eyebrow onDark>The verdict</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
              Every verification is a credential.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              A Treu verdict isn't a search result — it's a sealed judgment:
              a confidence score, the reasoning in plain language, and the
              sources that back it. Share it, and you're not arguing anymore.
            </p>
            <ul className="space-y-4">
              {[
                'Confidence scored against source credibility',
                'Plain-language reasoning you can quote',
                'Citations attached — no “trust me”',
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-white/80 text-[0.95rem]">
                  <svg className="w-5 h-5 text-accent-300 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {line}
                </li>
              ))}
            </ul>
          </div>

          {/* Static verdict card exhibit */}
          <div className="flex justify-center lg:justify-end">
            <div className="w-full max-w-[420px] rounded-xl bg-white border border-paper-200 shadow-overlay p-6">
              <div className="text-xs text-ink-400 mb-4">“Vitamin C prevents the common cold”</div>
              <div className="flex items-center gap-4">
                <VerdictSeal confidence={88} tone="unverified" size={64} />
                <div>
                  <div className="font-display text-lg font-semibold text-verdict-unverified">Limited support of the claim</div>
                  <div className="font-display text-[0.65rem] uppercase tracking-[0.18em] text-ink-400 mt-0.5">
                    88% confidence in our analysis
                  </div>
                </div>
              </div>
              <p className="mt-4 text-ink-700 text-sm leading-relaxed">
                Regular vitamin C doesn't prevent colds for most people — meta-analyses
                show at best a slight reduction in duration, not incidence.
              </p>
              <div className="mt-4 pt-4 border-t border-paper-200 flex flex-wrap gap-2">
                {['Cochrane Review · 96', 'NIH · 97', 'BMJ · 94'].map((s) => (
                  <span key={s} className="rounded-full bg-paper-50 border border-paper-200 px-3 py-1 text-xs text-ink-600">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-paper-50">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <Eyebrow>Capabilities</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-4">
              Everything you need to stay right.
            </h2>
            <p className="text-ink-600 text-lg leading-relaxed max-w-[520px] mx-auto">
              Treu gives you the tools to verify smarter, not harder.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="bg-white p-7 rounded-lg border border-paper-200 shadow-card">
                <h3 className="text-[1.0625rem] font-semibold text-ink-900 mb-2.5">
                  {feature.title}
                </h3>
                <p className="text-ink-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────── */}
      <section id="pricing" className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-[1200px] mx-auto text-center">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-ink-900 mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-ink-600 text-lg leading-relaxed max-w-[500px] mx-auto mb-14">
            Start free. Upgrade when you need more verifications or sources.
          </p>
          <div className="flex flex-wrap justify-center items-stretch gap-6">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.tier}
                className={`relative w-full max-w-[320px] sm:w-[260px] p-8 rounded-lg flex flex-col ${
                  plan.popular
                    ? 'border-2 border-accent-500 bg-accent-100/30 shadow-overlay md:scale-[1.03]'
                    : 'border border-paper-200 bg-white shadow-card'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-5 py-1 rounded-full bg-accent-500 text-white text-xs font-semibold">
                    Most Popular
                  </div>
                )}
                <div className={`text-center mb-6 ${plan.popular ? 'pt-2' : ''}`}>
                  <h3 className="text-xl font-bold text-ink-900 mb-3">
                    {plan.name}
                  </h3>
                  <div className="mb-3">
                    {plan.price === 0 ? (
                      <span className="font-display text-4xl font-light text-ink-900">Free</span>
                    ) : (
                      <>
                        <span className="font-display text-4xl font-light text-ink-900">${plan.price}</span>
                        <span className="text-ink-400 text-sm">/month</span>
                      </>
                    )}
                  </div>
                  <p className="text-ink-600 text-xs leading-normal">{plan.description}</p>
                </div>
                <ul className="list-none p-0 m-0 mb-7 flex-grow">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 mb-2.5 text-xs text-ink-700 text-left">
                      <svg className="w-4 h-4 text-accent-500 flex-shrink-0 mt-px" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  className={`block w-full py-3.5 rounded-md text-center text-sm font-semibold no-underline box-border transition-colors duration-150 ${
                    plan.popular
                      ? 'bg-brand-950 text-white hover:bg-brand-900'
                      : 'bg-transparent text-brand-950 border-2 border-brand-950 hover:bg-brand-50'
                  }`}
                >
                  {plan.price === 0 ? 'Get Started Free' : `Get ${plan.name}`}
                </Link>
              </div>
            ))}
          </div>
          <p className="text-ink-400 text-xs mt-8">
            All paid plans billed monthly. Cancel anytime. No long-term contracts.
          </p>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      <section className="bg-brand-950 py-16 md:py-20 px-4 md:px-8 text-center">
        <div className="max-w-[600px] mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">
            The next claim you hear — verify it.
          </h2>
          <p className="text-white/75 text-[1.0625rem] leading-relaxed mb-8">
            Sign in with Google or Apple and start verifying for free.
          </p>
          <Link
            to="/login"
            className="inline-block px-10 py-4 rounded-md text-base font-semibold text-white bg-accent-500 hover:bg-accent-600 no-underline transition-colors duration-150"
          >
            Get Started Free
          </Link>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="bg-brand-950 border-t border-white/10 py-10 px-4 md:px-8 text-center">
        <img src="/assets/logo-white.png" alt="Treu" className="h-6 w-auto mx-auto mb-6 opacity-70" />
        <div className="flex flex-wrap justify-center gap-8 mb-6">
          <Link to="/terms" className="text-white/50 hover:text-white/70 no-underline text-sm transition-colors duration-150">
            Terms of Service
          </Link>
          <Link to="/privacy" className="text-white/50 hover:text-white/70 no-underline text-sm transition-colors duration-150">
            Privacy Policy
          </Link>
          <Link to="/contact" className="text-white/50 hover:text-white/70 no-underline text-sm transition-colors duration-150">
            Contact
          </Link>
        </div>
        <FooterCopyright variant="full" className="text-white/40 !text-xs" />
      </footer>

    </div>
  )
}

export default LandingPage
